/**
 * Renders a single buyer-profile question — label and the type-
 * appropriate control (multiselect / range / text), including per-segment
 * expansion. Shared by the Phase 1 form (`BuyerProfileForm`) and the Phase 2
 * onboarding wizard so the question UI lives in exactly one place.
 */
import React from 'react';
import { format } from 'date-fns';
import { Form, Icon, TextArea } from 'semantic-ui-react';
import {
  AnswerPayload,
  MultiselectAnswer,
  PreferenceLevel,
  QuestionDef,
  RangeAnswer,
  TextAnswer,
} from './types';
import { AnswersMap, answerKey, segmentsForQuestion } from './answersState';
import { visibleOptionsForQuestion } from './displayRules';
import PreferenceSelector, { NestedRow, PreferenceColumn } from './PreferenceSelector';
import RangeQuestion from './RangeQuestion';
import GeographySelector from './GeographySelector';

interface Props {
  question: QuestionDef;
  answers: AnswersMap;
  /** Market-segment option value → label, for per-segment column headings. */
  segmentLabels: Record<string, string>;
  /**
   * Answers keyed by question key (the display-rule input) — used to apply a
   * question's `optionGroupFilter`. Optional; an absent map filters nothing.
   */
  answersByKey?: Record<string, unknown>;
  /**
   * Group value → sub-heading label for grouped options (e.g. state code →
   * state name on utility zones). Passed through to PreferenceSelector.
   */
  groupLabels?: Record<string, string>;
  readOnly?: boolean;
  /** Optional DOM id, used by the form's table-of-contents scroll links. */
  domId?: string;
  /** ISO timestamp this question's answer was last saved. */
  updatedAt?: string;
  onChange: (questionKey: string, segment: string | null, answer: AnswerPayload) => void;
  /**
   * Other questions whose `nestUnder.question === question.key`. Their
   * options are rendered as indented sub-rows inside this card's preference
   * grid rather than as their own top-level cards.
   */
  nestedChildren?: QuestionDef[];
}

/**
 * Build the flat NestedRow list PreferenceSelector renders inline beneath
 * its parent options. One row per (nested child question × child option),
 * each carrying a per-segment-column read/write into the child's stored
 * answer. The column ids must match the parent's column ids exactly so the
 * sub-rows line up under the parent columns.
 */
const buildNestedRows = (
  children: QuestionDef[],
  parentSegments: (string | null)[],
  answers: AnswersMap,
  onChange: (
    questionKey: string,
    segment: string | null,
    answer: AnswerPayload
  ) => void
): NestedRow[] => {
  const rows: NestedRow[] = [];
  for (const child of children) {
    if (!child.nestUnder || child.type !== 'multiselect') continue;
    for (const option of child.options ?? []) {
      const columns: NestedRow['columns'] = {};
      for (const segment of parentSegments) {
        // A non-per-segment child shares one answer across all parent
        // columns: read/write the same null-segment row.
        const childSegment = child.perSegment ? segment : null;
        const key = answerKey(child.key, childSegment);
        const current = (answers[key] as MultiselectAnswer | undefined) ?? {};
        columns[segment ?? 'main'] = {
          value: current[option.value],
          onChange: (next: PreferenceLevel | undefined) => {
            const updated: MultiselectAnswer = { ...current };
            if (next === undefined) delete updated[option.value];
            else updated[option.value] = next;
            onChange(child.key, childSegment, updated);
          },
        };
      }
      rows.push({
        id: `${child.key}:${option.value}`,
        parentOptionValue: child.nestUnder.option,
        label: option.label,
        columns,
      });
    }
  }
  return rows;
};

/**
 * One rendered geography editor. C&I and Utility are consolidated into a single
 * editor (front-end only) — its `segments` then lists every present segment so
 * an edit mirrors to each one's stored answer — while Community Solar keeps its
 * own market-scoped editor. The `label` joins the member segment names
 * (e.g. "Commercial & Industrial / Utility").
 */
interface GeoGroup {
  key: string;
  /** Segment heading; undefined when no market segment is selected. */
  label?: string;
  /** Segments this editor reads/writes — `[null]` when none selected. */
  segments: (string | null)[];
  communitySolar: boolean;
}

/** Market segments whose geography is consolidated into one shared editor. */
const CONSOLIDATED_SEGMENTS = ['CI', 'UTILITY'];

/**
 * Partition the geography question's segments into editors: one combined
 * C&I / Utility editor (whichever of the two are selected) plus a standalone
 * Community Solar editor, preserving the order segments first appear in.
 */
const buildGeoGroups = (
  segments: (string | null)[],
  segmentLabels: Record<string, string>
): GeoGroup[] => {
  const groups: GeoGroup[] = [];
  let combinedEmitted = false;
  for (const segment of segments) {
    if (segment == null) {
      groups.push({ key: '_', segments: [null], communitySolar: false });
    } else if (CONSOLIDATED_SEGMENTS.includes(segment)) {
      if (combinedEmitted) continue;
      combinedEmitted = true;
      const members = CONSOLIDATED_SEGMENTS.filter((s) => segments.includes(s));
      groups.push({
        key: members.join('+'),
        label: members.map((s) => segmentLabels[s] ?? s).join(' / '),
        segments: members,
        communitySolar: false,
      });
    } else {
      groups.push({
        key: segment,
        label: segmentLabels[segment] ?? segment,
        segments: [segment],
        communitySolar: segment === 'COMMUNITY_SOLAR',
      });
    }
  }
  return groups;
};

const QuestionCard: React.FC<Props> = ({
  question,
  answers,
  segmentLabels,
  answersByKey,
  groupLabels,
  readOnly,
  domId,
  updatedAt,
  onChange,
  nestedChildren,
}) => {
  const segments = segmentsForQuestion(question, answers);
  // Show the segment header (e.g. "Commercial & Industrial") whenever the
  // question is actually being rendered per-segment — even for a single
  // selected segment. It reinforces that the answer is scoped to that
  // segment, which matters once a second segment gets added later.
  const showSegmentHeader = segments.some((s) => s != null);

  // Range / text controls. Multiselect renders via PreferenceSelector below
  // (it handles its own per-segment columns).
  const renderControl = (segment: string | null) => {
    const current = answers[answerKey(question.key, segment)];

    if (question.type === 'range') {
      return (
        <RangeQuestion
          units={question.rangeUnits ?? ['MW', '$']}
          hideGradations={question.rangeHideGradations}
          value={(current as RangeAnswer) ?? {}}
          disabled={readOnly}
          onChange={(next) => onChange(question.key, segment, next)}
        />
      );
    }
    // Semantic UI's TextArea only picks up its styling inside a Form.
    return (
      <Form>
        <TextArea
          rows={4}
          disabled={readOnly}
          value={(current as TextAnswer)?.value ?? ''}
          onChange={(_e, d) =>
            onChange(question.key, segment, { value: String(d.value ?? '') })
          }
        />
      </Form>
    );
  };

  return (
    <div className="buyer-profile-question card" id={domId} key={question.key}>
      <div className="buyer-profile-question-label">
        {question.label}
        {updatedAt && (
          <span className="buyer-profile-question-updated">
            Last updated {format(new Date(updatedAt), 'MMMM d, yyyy')}
          </span>
        )}
      </div>
      {question.description && (
        <p className="buyer-profile-question-description">{question.description}</p>
      )}
      {question.visibilityNote && (
        <div className="buyer-profile-question-visibility-note">
          <Icon name="info circle" />
          {question.visibilityNote}
        </div>
      )}
      {question.type === 'multiselect' && question.key === 'geography' ? (
        // Geography uses the consolidated region/chip editor (issue #1926)
        // instead of the flat 50+ row list. C&I and Utility share one editor
        // that mirrors each edit to both segments' stored answers; Community
        // Solar keeps its own market-scoped editor.
        buildGeoGroups(segments, segmentLabels).map((group) => (
          <div className="buyer-profile-question-segment" key={group.key}>
            {showSegmentHeader && group.label && (
              <div className="buyer-profile-segment-heading">{group.label}</div>
            )}
            <GeographySelector
              options={visibleOptionsForQuestion(question, answersByKey ?? {})}
              value={
                (answers[
                  answerKey(question.key, group.segments[0])
                ] as MultiselectAnswer) ?? {}
              }
              disabled={readOnly}
              communitySolar={group.communitySolar}
              onChange={(next) =>
                group.segments.forEach((seg) => onChange(question.key, seg, next))
              }
            />
          </div>
        ))
      ) : question.type === 'multiselect' ? (
        // One column per segment — a single column when there are <2 market
        // segments, headed columns when there are more.
        <PreferenceSelector
          options={visibleOptionsForQuestion(question, answersByKey ?? {})}
          groupLabels={groupLabels}
          disabled={readOnly}
          columns={segments.map(
            (segment): PreferenceColumn => ({
              id: segment ?? 'main',
              header:
                showSegmentHeader && segment
                  ? segmentLabels[segment] ?? segment
                  : undefined,
              value: (answers[answerKey(question.key, segment)] as MultiselectAnswer) ?? {},
              onChange: (next) => onChange(question.key, segment, next),
            })
          )}
          nestedRows={buildNestedRows(
            nestedChildren ?? [],
            segments,
            answers,
            onChange
          )}
        />
      ) : (
        segments.map((segment) => (
          <div className="buyer-profile-question-segment" key={segment ?? '_'}>
            {showSegmentHeader && segment && (
              <div className="buyer-profile-segment-heading">
                {segmentLabels[segment] ?? segment}
              </div>
            )}
            {renderControl(segment)}
          </div>
        ))
      )}
    </div>
  );
};

export default QuestionCard;
